import validator from 'validator';
import { BiQuestionTemplate, reviewTemplate } from '../components/csqa/reviewTemplate';
export const defaultEditorValue = '<p> </p>';

type CsqaUserSubmittedFormData = {
    associateEmail: string;
    contactId: string;
    date: string;
    evaluationType: string;
    monitoringMethod: string;
    notes: string;
    supervisorEmail: string;
}

export type QualityAnswers = {
    [questionId: string]: {
        value: "Yes" | "No" | "N/A";
        subAttributes: { [subAttrId: string]: boolean }
    };
};

export type BiAnswers = {
    [questionId: string]: {
        value: string;
        subvalue?: string
    };
};

export type FilteredBiQuestion = {
    questionId: string;
    questionTemplate: BiQuestionTemplate;
};

// Valid Domains for URL
const validDomains = [
    'ringcs.lightning.force.com',
    'ringcs--beta.sandbox.lightning.force.com',
    'ringcs--develop.sandbox.lightning.force.com',
    'ringcs--gamma.sandbox.lightning.force.com',
];

// URL Validation
export const isValidUrl = (kustomerUrl: string) => {
    try {
        const url = new URL(kustomerUrl);
        const hostname = url.hostname;
        if (url.protocol === 'https:' && validDomains.some(domain => hostname === domain || hostname.endsWith('.' + domain))) {
            return true;
        }
        return false;
    } catch (_error) {
        return false;
    }
};

// Helper function to check if a quality question is valid.
const isQualityQuestionValid = (questionId: string, qualityAnswers: QualityAnswers) => {
    const answer = qualityAnswers[questionId];
    if (!answer?.value) return false;
    // For a "No" answer, at least one sub-attribute must be true.
    if (answer.value === "No") {
        return Object.values(answer.subAttributes).some(Boolean);
    }
    // "Yes" or "N/A" are acceptable answers.
    return answer.value === "Yes" || answer.value === "N/A";
};

// Helper function to return an array of invalid question IDs.
const getInvalidQualityQuestions = (qualityAnswers: QualityAnswers, qualityQuestionIds: string[]) =>
    qualityQuestionIds.filter((questionId) => !isQualityQuestionValid(questionId, qualityAnswers));

export const getCsqaValidationRules = (
    {
        associateEmail,
        contactId,
        date,
        evaluationType,
        monitoringMethod,
        notes,
        supervisorEmail,
    }: CsqaUserSubmittedFormData,
    qualityAnswers: QualityAnswers,
    biAnswers: BiAnswers,
    filteredBiQuestions: FilteredBiQuestion[]
) => {
    return [
        {
            test: () => !!associateEmail,
            message: 'Please Provide Agent Associated',
        },
        {
            test: () => !(associateEmail && !supervisorEmail),
            message: `The Agent you selected: ${associateEmail}, does not have a Supervisor associated. Please reach out to wfm@ring.com to resolve`,
        },
        {
            test: () => !!evaluationType,
            message: 'Please Provide the Evaluation Type',
        },
        {
            test: () => !!monitoringMethod,
            message: 'Please Provide the Monitoring Method',
        },
        {
            test: () => validator.isDate(date),
            message: 'Invalid Date',
        },
        {
            test: () => !!contactId,
            message: 'Please Provide the Contact ID Associated',
        },
        {
            test: () => validator.isUUID(contactId) || (/^\d{1,16}$/.test(contactId)),
            message: 'Invalid Contact ID or Sprinklr Case ID',
        },
        {
            test: () => !(notes === '' || notes === defaultEditorValue),
            message: 'Notes Field Cannot be Empty',
        },
        // Quality Questions validation
        {
            test: () => {
                const qualityQuestionIds = Object.keys(reviewTemplate.qualityQuestions);
                return qualityQuestionIds.every((questionId) =>
                    isQualityQuestionValid(questionId, qualityAnswers)
                );
            },
            message: (() => {
                const qualityQuestionIds = Object.keys(reviewTemplate.qualityQuestions);
                const invalidQuestions = getInvalidQualityQuestions(qualityAnswers, qualityQuestionIds);

                if (invalidQuestions.length === 0) {
                    return "Quality Questions validation error.";
                }

                // Remove the "1." prefix if present.
                const formattedQuestions = invalidQuestions.map((questionId) =>
                    questionId.replace(/^1\./, '')
                );
                const questionLabel = invalidQuestions.length > 1 ? 'Questions' : 'Question';

                return `For questions answered 'No', select at least one reason. Please complete ${questionLabel} ${formattedQuestions.join(', ')}.`;
            })(),
        },
        // BI Questions validation
        {
            test: () => {
                return filteredBiQuestions.every(biQuestion => {
                    const answer = biAnswers[biQuestion.questionId];
                    if (!answer || !answer.value || answer.value.trim() === '') return false;
                    // If the selected option requires a supporting subvalue then ensure it’s provided.
                    const template = reviewTemplate.biQuestions[biQuestion.questionId];
                    if (template && template.options) {
                        // Find the option that matches the answer
                        const option = Object.values(template.options).find(
                            option => option.label === answer.value
                        );
                        if (option && option.subValues && option.subValues.length > 0) {
                            return answer.subvalue && answer.subvalue.trim().length > 0;
                        }
                    }
                    return true;
                });
            },
            message: "Please answer all Business Impact questions completely.",
        },
    ];
};